/**
 * Card field-shape sanitizer.
 *
 * The cards entity schema (`shared/sync-engine/schema/entities.ts`) declares
 * `back`, `source`, `notes`, and `sortKey` as `z.string().optional()`. Some
 * legacy rows in old accounts carry these as `[]` (empty array) instead of
 * undefined — the in-app symptom is a storm of
 * `Invalid payload for cards-bulk-push: Expected string, received array`
 * on every reconnect, which trips rate-limiting and prevents the dashboard
 * from settling.
 *
 * This sanitizer drops the field when its runtime type isn't string-ish,
 * so the schema-shaped value reaches the wire. Cleaning is idempotent and
 * cheap (a few key reads); it never invents data, only removes wrong
 * shapes. Required string fields (`id`, `front`, `createdTimeISO`) are
 * intentionally NOT touched — a wrong shape there means the card is
 * structurally broken and silently dropping the field would mask the bug.
 *
 * Applied at three boundaries so a malformed entry can't survive any
 * round-trip: cold hydrate (Dexie → Zustand), local writes
 * (`cleanCardForStore`), and sync-diff arrival (server → Zustand/Dexie).
 * Once outbound pushes are clean the server stores cleaned values and
 * broadcasts them back, healing the row everywhere on its own.
 */

const OPTIONAL_STRING_FIELDS = ['back', 'source', 'notes', 'sortKey'] as const;

/** True when at least one optional-string field has the wrong runtime type. */
export function hasMalformedStringField(card: Record<string, unknown>): boolean {
  for (const f of OPTIONAL_STRING_FIELDS) {
    const v = card[f];
    if (v !== undefined && v !== null && typeof v !== 'string') return true;
  }
  return false;
}

/** Remove any optional-string field whose value isn't a string. Mutates `out` in place. */
export function stripMalformedStringFields(out: Record<string, unknown>): void {
  for (const f of OPTIONAL_STRING_FIELDS) {
    const v = out[f];
    if (v !== undefined && v !== null && typeof v !== 'string') {
      delete out[f];
    }
  }
}
